import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import {
  LayoutDashboard, TrendingUp, FolderKanban, Receipt, Building2,
  DollarSign, BarChart3, Settings, LogOut, Menu, X, ChevronDown, Users, FileText, Wrench, Shield, Activity,
  AlertTriangle, FileCheck, GitBranch, ClipboardList, Car, Lock
} from 'lucide-react';

const NAV_ITEMS = [
  { label: 'Dashboard', path: '/dashboard', icon: LayoutDashboard, roles: ['DIRECTOR', 'SALES', 'ACCOUNTANT', 'VIEWER', 'SUPERVISOR', 'HR_ADMIN'] },
  { label: 'Sales Pipeline', path: '/sales', icon: TrendingUp, roles: ['DIRECTOR', 'SALES'] },
  { label: 'Projects', path: '/projects', icon: FolderKanban, roles: ['DIRECTOR', 'SALES', 'ACCOUNTANT', 'SUPERVISOR', 'HR_ADMIN', 'VIEWER'] },
  { label: 'DWR', path: '/dwr', icon: FileText, roles: ['DIRECTOR', 'SUPERVISOR', 'HR_ADMIN'] },
  { label: 'My TA/DA', path: '/my-tada', icon: Car, roles: ['SALES', 'DIRECTOR', 'HR_ADMIN', 'SUPERVISOR'] },
  { label: 'Employees', path: '/employees', icon: Users, roles: ['DIRECTOR', 'HR_ADMIN', 'ACCOUNTANT'] },
  { label: 'Tools & Inventory', path: '/tools', icon: Wrench, roles: ['DIRECTOR', 'SUPERVISOR', 'ACCOUNTANT', 'HR_ADMIN'] },
  { label: 'AMC Contracts', path: '/amc', icon: Shield, roles: ['DIRECTOR', 'SUPERVISOR', 'HR_ADMIN'] },
  { label: 'PO & Invoices', path: '/po-invoices', icon: ClipboardList, roles: ['DIRECTOR', 'ACCOUNTANT', 'HR_ADMIN'] },
  { label: 'Expenses', path: '/expenses', icon: Receipt, roles: ['DIRECTOR', 'ACCOUNTANT', 'SUPERVISOR', 'HR_ADMIN'] },
  { label: 'Overheads', path: '/overheads', icon: Building2, roles: ['DIRECTOR', 'ACCOUNTANT', 'HR_ADMIN'] },
  { label: 'Revenue', path: '/revenue', icon: DollarSign, roles: ['DIRECTOR', 'ACCOUNTANT', 'HR_ADMIN'] },
  { label: 'Safety Incidents', path: '/incidents', icon: AlertTriangle, roles: ['DIRECTOR', 'SUPERVISOR', 'HR_ADMIN'] },
  { label: 'Handover', path: '/handover', icon: FileCheck, roles: ['DIRECTOR', 'HR_ADMIN'] },
  { label: 'Directory', path: '/directory', icon: GitBranch, roles: ['DIRECTOR', 'SALES', 'ACCOUNTANT', 'SUPERVISOR', 'HR_ADMIN', 'VIEWER'] },
  { label: 'Reports', path: '/reports', icon: BarChart3, roles: ['DIRECTOR', 'ACCOUNTANT', 'HR_ADMIN', 'VIEWER'] },
  { label: 'Audit Trail', path: '/audit-log', icon: Activity, roles: ['DIRECTOR', 'HR_ADMIN'] },
  { label: 'Settings', path: '/settings', icon: Settings, roles: ['DIRECTOR', 'HR_ADMIN'] },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showChangePw, setShowChangePw] = useState(false);
  const [pwForm, setPwForm] = useState({ current_password: '', new_password: '', confirm_password: '' });
  const [pwSaving, setPwSaving] = useState(false);
  const [pwError, setPwError] = useState('');

  const filteredNav = NAV_ITEMS.filter(item => item.roles.includes(user?.role));

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const handleChangePw = async (e) => {
    e.preventDefault();
    setPwError('');
    if (pwForm.new_password !== pwForm.confirm_password) { setPwError('Passwords do not match'); return; }
    if (pwForm.new_password.length < 8) { setPwError('Min 8 characters'); return; }
    setPwSaving(true);
    try {
      const api = (await import('@/services/api')).default;
      await api.post('/auth/change-password', { current_password: pwForm.current_password, new_password: pwForm.new_password });
      setShowChangePw(false);
      setPwForm({ current_password: '', new_password: '', confirm_password: '' });
      alert('Password changed successfully');
    } catch (err) { setPwError(err.response?.data?.detail || 'Failed'); }
    finally { setPwSaving(false); }
  };

  const roleBadge = {
    DIRECTOR: 'bg-[#1B5486] text-white',
    SALES: 'bg-emerald-600 text-white',
    ACCOUNTANT: 'bg-amber-600 text-white',
    VIEWER: 'bg-slate-500 text-white',
  };

  return (
    <div className="flex h-screen bg-[#F8FAFC]" data-testid="app-layout">
      {/* Sidebar */}
      <aside className={`fixed inset-y-0 left-0 z-50 w-64 bg-white border-r border-slate-200 transform transition-transform duration-200 lg:translate-x-0 lg:static lg:inset-auto flex flex-col ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}
        data-testid="sidebar">
        <div className="flex items-center gap-3 px-5 h-16 border-b border-slate-200 flex-shrink-0">
          <img src="/logo.png" alt="Pace Infocom" className="h-8 w-auto" />
          <div>
            <h1 className="text-sm font-semibold text-slate-900 font-heading tracking-tight">PaceOps</h1>
            <p className="text-[10px] text-slate-500 uppercase tracking-wider">Operations Simplified</p>
          </div>
          <button className="lg:hidden ml-auto" onClick={() => setSidebarOpen(false)}>
            <X size={20} className="text-slate-500" />
          </button>
        </div>

        <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
          {filteredNav.map(item => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path || location.pathname.startsWith(item.path + '/');
            return (
              <Link key={item.path} to={item.path}
                data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, '-')}`}
                onClick={() => setSidebarOpen(false)}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-all duration-200
                  ${isActive ? 'bg-[#1B5486]/10 text-[#1B5486] font-medium' : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'}`}>
                <Icon size={18} strokeWidth={isActive ? 2 : 1.5} />
                {item.label}
              </Link>
            );
          })}
        </nav>

        <div className="border-t border-slate-200 p-4 flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-[#1B5486]/10 flex items-center justify-center">
              <span className="text-[#1B5486] font-semibold text-sm">{user?.name?.charAt(0) || 'U'}</span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-slate-900 truncate">{user?.name}</p>
              <span className={`inline-block text-[10px] px-1.5 py-0.5 rounded-full ${roleBadge[user?.role] || 'bg-slate-100 text-slate-600'}`}>
                {user?.role}
              </span>
            </div>
            <button onClick={() => setShowChangePw(true)} data-testid="change-password-button" title="Change Password" className="text-slate-400 hover:text-[#1B5486] transition-colors">
              <Lock size={16} />
            </button>
            <button onClick={handleLogout} data-testid="logout-button" className="text-slate-400 hover:text-red-500 transition-colors">
              <LogOut size={18} />
            </button>
          </div>
        </div>
      </aside>

      {/* Change Password Modal */}
      {showChangePw && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40" onClick={() => setShowChangePw(false)}>
          <div className="bg-white rounded-lg w-full max-w-sm p-6" onClick={e => e.stopPropagation()} data-testid="change-password-modal">
            <h2 className="text-lg font-semibold text-slate-900 mb-4">Change Password</h2>
            <form onSubmit={handleChangePw} className="space-y-3">
              <div><label className="block text-xs font-medium text-slate-600 mb-1">Current Password</label>
                <input type="password" value={pwForm.current_password} onChange={e => setPwForm({...pwForm, current_password: e.target.value})} required className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-[#1B5486]/20 outline-none" data-testid="current-password-input" /></div>
              <div><label className="block text-xs font-medium text-slate-600 mb-1">New Password</label>
                <input type="password" value={pwForm.new_password} onChange={e => setPwForm({...pwForm, new_password: e.target.value})} required className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-[#1B5486]/20 outline-none" data-testid="new-password-input" /></div>
              <div><label className="block text-xs font-medium text-slate-600 mb-1">Confirm Password</label>
                <input type="password" value={pwForm.confirm_password} onChange={e => setPwForm({...pwForm, confirm_password: e.target.value})} required className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-[#1B5486]/20 outline-none" data-testid="confirm-password-input" /></div>
              {pwError && <p className="text-xs text-red-500">{pwError}</p>}
              <div className="flex gap-2 pt-1">
                <button type="button" onClick={() => setShowChangePw(false)} className="flex-1 border border-slate-200 py-2 rounded-md text-sm">Cancel</button>
                <button type="submit" disabled={pwSaving} data-testid="submit-change-password" className="flex-1 bg-[#1B5486] text-white py-2 rounded-md text-sm">{pwSaving ? 'Saving...' : 'Change'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Overlay */}
      {sidebarOpen && <div className="fixed inset-0 z-40 bg-black/30 lg:hidden" onClick={() => setSidebarOpen(false)} />}

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-16 bg-white border-b border-slate-200 flex items-center px-4 lg:px-8 gap-4">
          <button className="lg:hidden" onClick={() => setSidebarOpen(true)} data-testid="menu-toggle">
            <Menu size={22} className="text-slate-600" />
          </button>
          <div className="flex-1" />
          <div className="flex items-center gap-3">
            <span className="text-sm text-slate-500 hidden sm:block">{user?.email}</span>
          </div>
        </header>

        <main className="flex-1 overflow-y-auto overflow-x-hidden p-4 lg:p-6">
          {children}
        </main>
      </div>
    </div>
  );
}
